import { useQuery } from "@apollo/client"
import ForecastedFuel, { MinWeightProps } from "../../../figures/ForecastedFuel"
import {
  endOfDay,
  removeDays,
  startOfDay,
  toTimestamp,
  nowDateTime,
  IANATimeZone,
} from "@hydrologiq/common/helpers/date"
import { TimePoint, convertTimeSerieData, convertTimeSeriesData } from "@hydrologiq/common/types/graph/timeseries"
import * as gqlTypes from "@hydrologiq/gql/graphql"
import { EnergyUnit } from "@hydrologiq/common/types/energy/consumption"
import { DateTime } from "timezonecomplete"
import { hydrogenForecast } from "@hydrologiq/shared/asset/attributes/rundry/hydrogen"

export interface ForecastedHydrogenFigureProps {
  title: string
  window: number
  threshold: number
  installationId: string
  installationEndDate: DateTime
  installationStartDate: DateTime
  minPressure?: number
  debug?: boolean
  weighted?: boolean
}

function ForecastedHydrogenFigure({
  title,
  window,
  threshold,
  installationId,
  installationEndDate,
  installationStartDate,
  minPressure,
  debug,
  weighted,
}: ForecastedHydrogenFigureProps) {
  const nowDate = nowDateTime()
  const installationEnded = nowDate.unixUtcMillis() >= installationEndDate.unixUtcMillis()
  const latestEndDate = installationEnded ? installationEndDate : nowDate
  const latestStartDate = removeDays(latestEndDate, window)
  const diffEndDate = latestEndDate
  const diffStartDate = removeDays(diffEndDate, window)

  const { loading, error, data } = useQuery(gqlTypes.HydrogenWeightForecastDownSampledDocument, {
    variables: {
      id: installationId,
      latestStart: toTimestamp(startOfDay(latestStartDate), true),
      latestStop: toTimestamp(installationEnded ? endOfDay(latestEndDate) : latestEndDate, true),
      diffStart: toTimestamp(startOfDay(diffStartDate), true),
      diffStop: toTimestamp(endOfDay(diffEndDate), true),
      installationStart: toTimestamp(startOfDay(installationStartDate), true),
      location: IANATimeZone(),
    },
  })

  let forecastedDays: number | null = null
  let currentWeight: TimePoint | undefined = undefined
  let minWeight: MinWeightProps | undefined = undefined
  if (data) {
    console.log(data)
    const diffValues = convertTimeSeriesData(data.difference as gqlTypes.TimeSeriesData[])

    currentWeight = convertTimeSerieData(data.weight as gqlTypes.TimeSeriesData)
    let weightToForecast = structuredClone(currentWeight)

    if (data.weightMin) {
      const weight = convertTimeSerieData(data.weightMin as gqlTypes.TimeSeriesData).value
      minWeight = { weight, pressure: minPressure }
      weightToForecast.value = weightToForecast.value - weight
    }
    forecastedDays = hydrogenForecast(weightToForecast, diffValues, threshold, weighted)
  }

  return (
    <ForecastedFuel
      heading={title}
      days={forecastedDays}
      debug={debug}
      unit={EnergyUnit.kg}
      currentLevel={currentWeight?.value}
      minWeight={minWeight}
      loading={loading}
      error={error ? error.message : undefined}
    />
  )
}

export default ForecastedHydrogenFigure
